import { useMutation } from "@apollo/client"
import { yupResolver } from "@hookform/resolvers/yup"
import cn from "classnames"
import { camelCase, pick } from "lodash-es"
import React, { useCallback, useEffect, useState } from "react"
import { type SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import CustomerForm, { type RegisterFormInputs, customerSchema } from "./customer_form"
import { graphql } from "@/api"
import { ClientRole } from "@/api/graphql"
import ClientData from "@/common/client_data"
import FormForm from "@/common/components/form_form"
import { localeCountry } from "@/common/locales"
import { Routemap } from "@/common/routes"
import Util from "@/common/util"

const registerDoc = graphql(`
  mutation CustomerRegister($customer: CustomerInput!) {
    customerRegister(customer: $customer) {
      success
      redirect
      message

      customer {
        id
        __typename
      }

      errors {
        message
        path
      }
    }
  }
`)

const schema = customerSchema(true)

const RegisterForm: React.FC = () => {
  const { t } = useTranslation()
  const [mutation] = useMutation(registerDoc)
  const formMethods = useForm<RegisterFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      country: localeCountry.toLocaleUpperCase(),
      deliveryCountry: localeCountry.toLocaleUpperCase(),
    },
  })
  const {
    handleSubmit,
    formState: { errors },
    setError,
    watch,
    register,
  } = formMethods
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<string>()
  const company = watch("company")
  const clientRole = watch("clientRole")
  const business = clientRole === ClientRole.business
  const disabledByClientRole = clientRole === null
  const germanCompany = !Util.isBlank(company) && localeCountry === "de"
  const [isChecked, setIsChecked] = useState(false)

  useEffect(() => {
    if (germanCompany) {
      setIsChecked(true)
    }
  }, [germanCompany])

  const submit: SubmitHandler<RegisterFormInputs> = useCallback(
    async (form, e) => {
      e?.preventDefault()

      setMessage(undefined)
      setLoading(true)

      const { ...customer } = pick(
        form,
        "firstName",
        "lastName",
        "company",
        "vatId",
        "orgId",
        "glnNumber",
        "reference",
        "street",
        "street2",
        "houseNumber",
        "zipcode",
        "city",
        "state",
        "country",
        "deliveryFirstName",
        "deliveryLastName",
        "deliveryCompany",
        "deliveryStreet",
        "deliveryStreet2",
        "deliveryHouseNumber",
        "deliveryZipcode",
        "deliveryCity",
        "deliveryState",
        "deliveryCountry",
        "phone1",
        "deliveryPhone",
        "invoiceEmail",
        "differentDeliveryAddress",
        "newsletterEnabled",
        "email",
        "password"
      )

      if (!business) {
        customer.orgId = undefined
        customer.glnNumber = undefined
      }

      const result = await mutation({
        variables: { customer: { ...customer, createAccount: true, registerCustomer: true } },
      })
      const data = result.data?.customerRegister

      if (data) {
        const errors = data.errors

        if (errors && errors.length > 0) {
          for (const error of errors) {
            if (error.path && error.path.length > 0) {
              setError(camelCase(error.path[0]), { type: "validate", message: error.message })
            }
          }

          setLoading(false)
          return
        }

        if (data.message) {
          setMessage(data.message)
          setLoading(false)
        } else if (data.redirect) {
          location.href = data.redirect
        } else if (data.success) {
          location.href = Routemap.customerZone(t)
        }
      }
    },
    [mutation, setError, t, business]
  )

  return (
    <FormForm
      id="new_customer"
      className={cn("new_customer", { "client-role-undefined": disabledByClientRole })}
      noValidate
      onSubmit={handleSubmit(submit)}
      formMethods={formMethods}
    >
      <CustomerForm />
      {errors.vatId && (
        <div className="vatalert">
          <div className="alert alert-error">{errors.vatId.message}</div>
        </div>
      )}
      {errors.orgId && (
        <div className="orgalert">
          <div className="alert alert-error">{errors.orgId.message}</div>
        </div>
      )}
      {errors.glnNumber && (
        <div className="glnalert">
          <div className="alert alert-error">{errors.glnNumber.message}</div>
        </div>
      )}
      {message && <p className="form_message bg-success">{message}</p>}
      {(germanCompany || Util.isBlank(company)) && (
        <div className="notice_snippet">
          <div id="newsletter_enabled_field" className="checkbox text">
            <span className="paragraph-xs bold">{t("order.subscribe_newsletter")}</span>
            <div className="relative">
              <div className="checkbox">
                <input
                  type="checkbox"
                  className="form-control"
                  id="newsletter_enabled"
                  {...register("newsletterEnabled")}
                  name="newsletterEnabled"
                  checked={isChecked}
                  onChange={() => setIsChecked(!isChecked)}
                  disabled={disabledByClientRole}
                />
                <label htmlFor="newsletter_enabled" className="checkbox-label paragraph-xs">
                  <div dangerouslySetInnerHTML={{ __html: ClientData.snippet("consent") }} />
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
      <button
        id="customer_register_new"
        className="button button-primary full-width"
        type="submit"
        disabled={loading}
        data-gtm="register-account-button"
      >
        {t("customer.register")}
      </button>
      <div className="notice_snippet" dangerouslySetInnerHTML={{ __html: ClientData.snippet("customer") }} />
    </FormForm>
  )
}

export default RegisterForm
